import { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/layout';
import { WhatsNewModal } from './components/WhatsNewModal';
import {
  Dashboard,
  Connections,
  Bots,
  Flows,
  FlowBuilder,
  Settings,
  Home,
  Privacy,
  Terms,
  Subscription,
  Admin,
} from './pages';
import { bitrix24 } from './lib/bitrix24';
import { useAppStore } from './stores/appStore';
import { api } from './lib/api';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { AnimatePresence, motion } from 'framer-motion';
import './index.css';

const APP_LOADING_LOTTIE_URL =
  'https://assets-v2.lottiefiles.com/a/427209d4-118a-11ee-af13-efdc31b1068b/w8KYRbsm9k.lottie';
const minAppLoadingMsEnv = Number(import.meta.env.VITE_APP_LOADING_MIN_MS);
const MIN_APP_LOADING_MS = Number.isFinite(minAppLoadingMsEnv) && minAppLoadingMsEnv >= 0
  ? minAppLoadingMsEnv
  : 2000;
const tenantSyncIntervalMsEnv = Number(import.meta.env.VITE_TENANT_SYNC_INTERVAL_MS);
const TENANT_SYNC_INTERVAL_MS = Number.isFinite(tenantSyncIntervalMsEnv) && tenantSyncIntervalMsEnv >= 5000
  ? tenantSyncIntervalMsEnv
  : 120000;
const bitrixAuthSyncIntervalMsEnv = Number(import.meta.env.VITE_BITRIX_AUTH_SYNC_INTERVAL_MS);
const BITRIX_AUTH_SYNC_INTERVAL_MS =
  Number.isFinite(bitrixAuthSyncIntervalMsEnv) && bitrixAuthSyncIntervalMsEnv >= 5 * 60000
    ? bitrixAuthSyncIntervalMsEnv
    : 10 * 60 * 1000;
const bitrixAutoResizeEnv = String(import.meta.env.VITE_BITRIX_AUTO_RESIZE || '').toLowerCase();
const ENABLE_BITRIX_AUTO_RESIZE = bitrixAutoResizeEnv === '1' || bitrixAutoResizeEnv === 'true';
const bitrixLifecycleDebugEnv = String(import.meta.env.VITE_BITRIX_LIFECYCLE_DEBUG || '').toLowerCase();
const ENABLE_BITRIX_LIFECYCLE_DEBUG =
  bitrixLifecycleDebugEnv === '1' || bitrixLifecycleDebugEnv === 'true';
const RELOAD_TRACE_KEY = 'bcb_reload_trace_v1';
const RELOAD_TRACE_TTL_MS = 30000;

type BitrixAuthSyncPayload = {
  domain: string;
  member_id?: string;
  auth: {
    access_token: string;
    refresh_token?: string;
    expires_in?: number | string;
    domain?: string;
    member_id?: string;
    user_id?: string | number;
    scope?: string;
  };
};

type ReloadTrace = {
  source: string;
  atIso: string;
  atMs: number;
  href: string;
};

const readReloadTrace = (): ReloadTrace | null => {
  try {
    const raw = sessionStorage.getItem(RELOAD_TRACE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as ReloadTrace;
  } catch {
    return null;
  }
};

const writeReloadTrace = (source: string) => {
  try {
    const payload: ReloadTrace = {
      source,
      atIso: new Date().toISOString(),
      atMs: Date.now(),
      href: window.location.href,
    };
    sessionStorage.setItem(RELOAD_TRACE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage failures in restricted environments.
  }
};

const clearReloadTrace = () => {
  try {
    sessionStorage.removeItem(RELOAD_TRACE_KEY);
  } catch {
    // Ignore storage failures in restricted environments.
  }
};

const readBitrixAuthFromUrl = (
  params: URLSearchParams,
): BitrixAuthSyncPayload | null => {
  const accessToken = String(
    params.get('AUTH_ID') || params.get('access_token') || '',
  ).trim();
  const refreshToken = String(
    params.get('REFRESH_ID') || params.get('refresh_token') || '',
  ).trim();
  const domain = String(params.get('DOMAIN') || params.get('domain') || '')
    .trim()
    .toLowerCase();
  const memberId = String(
    params.get('member_id') || params.get('MEMBER_ID') || '',
  ).trim();
  const expiresIn = String(
    params.get('AUTH_EXPIRES') || params.get('expires_in') || '',
  ).trim();
  const scope = String(params.get('scope') || '').trim();
  const userId = String(
    params.get('user_id') || params.get('USER_ID') || '',
  ).trim();

  if (!accessToken || !domain) {
    return null;
  }

  return {
    domain,
    member_id: memberId || undefined,
    auth: {
      access_token: accessToken,
      refresh_token: refreshToken || undefined,
      expires_in: expiresIn || undefined,
      domain,
      member_id: memberId || undefined,
      scope: scope || undefined,
      user_id: userId || undefined,
    },
  };
};

const shouldRefreshBitrixIframeAuth = (
  auth?: BitrixAuthSyncPayload['auth'] | null,
): boolean => {
  if (!auth?.access_token) {
    return false;
  }

  // Some Bitrix iframe auth payloads can expose a short-lived access token
  // without the refresh token. That is not enough to heal the backend OAuth
  // chain after invalid_grant, so force BX24.refreshAuth() in that case.
  if (!String(auth.refresh_token || '').trim()) {
    return true;
  }

  const expiresIn = Number(auth?.expires_in);
  return Number.isFinite(expiresIn) && expiresIn > 0 && expiresIn <= 10 * 60;
};

function App() {
  const { fetchAll, tenant, setTenant, error: storeError } = useAppStore();
  const [debugInfo, setDebugInfo] = useState<string>('Initializing...');
  const [initError, setInitError] = useState<string | null>(null);
  const [initComplete, setInitComplete] = useState(false);

  const initialized = useRef(false);
  const loadingStartedAt = useRef<number>(Date.now());
  const initCompleteTimeout = useRef<number | null>(null);
  const tenantSyncInFlight = useRef(false);
  const bitrixAuthSyncInterval = useRef<number | null>(null);
  const lastBitrixAuthSyncReason = useRef<string | null>(null);

  const triggerAppReload = (reason: string) => {
    writeReloadTrace(`app:${reason}`);
    window.location.reload();
  };

  useEffect(() => {
    if (!ENABLE_BITRIX_LIFECYCLE_DEBUG) {
      return;
    }

    const onPopState = (event: PopStateEvent) => {
      console.warn('[Navigation] popstate fired', { state: event.state, url: window.location.href });
    };

    const onHashChange = () => {
      console.warn('[Navigation] hashchange fired', { url: window.location.href });
    };

    const previousTrace = readReloadTrace();
    if (previousTrace) {
      const ageMs = Date.now() - previousTrace.atMs;
      if (ageMs <= RELOAD_TRACE_TTL_MS) {
        const probableSource = previousTrace.source.startsWith('app:')
          ? 'app'
          : 'bitrix-container-or-browser';
        console.warn(
          `[Reload Diagnosis] probable source=${probableSource} | previous=${previousTrace.source} | ageMs=${ageMs} | from=${previousTrace.href}`,
        );
      }
      clearReloadTrace();
    }

    const logLifecycle = (event: string) => {
      const navEntry = performance.getEntriesByType('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined;
      const navType = navEntry?.type || 'unknown';
      const nowIso = new Date().toISOString();
      const state = document.visibilityState;
      console.info(`[Lifecycle] ${event} | at=${nowIso} | visibility=${state} | nav=${navType}`);
    };

    const onBeforeUnload = () => {
      const trace = readReloadTrace();
      if (!trace?.source.startsWith('app:')) {
        writeReloadTrace('external:beforeunload');
      }
      logLifecycle('beforeunload');
    };
    const onPageHide = () => {
      const trace = readReloadTrace();
      if (!trace?.source.startsWith('app:')) {
        writeReloadTrace('external:pagehide');
      }
      logLifecycle('pagehide');
    };
    const onPageShow = () => logLifecycle('pageshow');
    const onVisibilityChange = () => logLifecycle('visibilitychange');

    logLifecycle('lifecycle-debug-enabled');
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('popstate', onPopState);
    window.addEventListener('hashchange', onHashChange);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    loadingStartedAt.current = Date.now();

    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (navigationEntry?.type) {
      console.info(`[App Boot] navigation type: ${navigationEntry.type}`);
    }

    const syncBitrixAuthPayload = async (
      payload: BitrixAuthSyncPayload | null,
      source: string,
    ): Promise<boolean> => {
      if (!payload) {
        return false;
      }

      try {
        const result = await api.syncBitrixAppAuth(payload);
        // Save token immediately — backend returns it even when tokens are unchanged
        if (result?.tenantToken) {
          localStorage.setItem('auth_token', result.tenantToken);
        }
        if (!result?.updated) {
          lastBitrixAuthSyncReason.current = result?.reason || 'not_updated';
          console.warn(
            `Bitrix OAuth sync not updated (${source}): ${result?.reason || 'not_updated'}`,
          );
          return false;
        }
        lastBitrixAuthSyncReason.current = null;
        return true;
      } catch (syncError) {
        lastBitrixAuthSyncReason.current = 'request_failed';
        console.warn(`Bitrix OAuth sync failed (${source}, non-blocking):`, syncError);
        return false;
      }
    };

    const syncBitrixSdkAuth = async (
      source: string,
      options: { refreshIfNeeded?: boolean; forceRefresh?: boolean } = {},
    ): Promise<boolean> => {
      if (!bitrix24.isInBitrix24()) {
        return false;
      }

      try {
        await bitrix24.init();
      } catch (error) {
        console.warn(`Bitrix24 SDK init failed during OAuth sync (${source}):`, error);
        if (!bitrix24.isInBitrix24()) {
          return false;
        }
      }

      const currentAuth = await bitrix24.getAuthData();
      const currentSynced = await syncBitrixAuthPayload(
        currentAuth?.access_token && currentAuth?.domain
          ? {
              domain: currentAuth.domain,
              member_id: currentAuth.member_id,
              auth: currentAuth,
            }
          : null,
        `${source}/sdk-current`,
      );

      const shouldRefresh =
        options.forceRefresh === true ||
        lastBitrixAuthSyncReason.current === 'refresh_token_required_to_heal_invalid_grant' ||
        (options.refreshIfNeeded !== false && shouldRefreshBitrixIframeAuth(currentAuth));

      if (!shouldRefresh) {
        return currentSynced;
      }

      try {
        const refreshedAuth = await bitrix24.refreshAuth();
        const refreshedSynced = await syncBitrixAuthPayload(
          refreshedAuth?.access_token && refreshedAuth?.domain
            ? {
                domain: refreshedAuth.domain,
                member_id: refreshedAuth.member_id,
                auth: refreshedAuth,
              }
            : null,
          `${source}/sdk-refresh`,
        );
        return refreshedSynced || currentSynced;
      } catch (error) {
        console.warn(`Bitrix OAuth refresh failed (${source}, non-blocking):`, error);
        return currentSynced;
      }
    };

    if (window.location.pathname.startsWith('/admin')) {
      const bootAdminMode = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        await syncBitrixAuthPayload(readBitrixAuthFromUrl(urlParams), 'admin/url');
        await syncBitrixSdkAuth('admin', { refreshIfNeeded: true });
      };

      bootAdminMode().finally(() => {
        setDebugInfo('Admin mode');
        setInitComplete(true);
      });

      return () => {
        if (bitrixAuthSyncInterval.current !== null) {
          window.clearInterval(bitrixAuthSyncInterval.current);
          bitrixAuthSyncInterval.current = null;
        }
      };
    }

    // Initialize Bitrix24 SDK if running inside iframe
    const initBitrix = async () => {
      try {
        setDebugInfo('Checking Bitrix24 context...');

        // Check for saved tenantId first (for page refreshes)
        const savedTenantId = localStorage.getItem('tenantId');
        
        // Check for connectionId in URL (from install redirect)
        const urlParams = new URLSearchParams(window.location.search);
        // Fallback token sync from iframe URL query params. This keeps OAuth healthy
        // even when BX24 SDK init is delayed/failing.
        await syncBitrixAuthPayload(readBitrixAuthFromUrl(urlParams), 'url');
        const connectionId = urlParams.get('connectionId');

        if (connectionId) {
            setDebugInfo('Carregando ambiente...');
            try {
                const connection = await api.getConnection(connectionId);
                if (connection && connection.tenantId) {
                    console.log('Found tenant from connection:', connection.tenantId);
                    localStorage.setItem('tenantId', connection.tenantId);
                  localStorage.removeItem('actingTenantId');
                    // Clean URL
                    window.history.replaceState({}, '', window.location.pathname);
                    await fetchAll(connection.tenantId);
                    return;
                }
            } catch (e) {
                console.error('Failed to fetch connection:', e);
                setDebugInfo(`Error fetching connection: ${e}`);
                setInitError(`Failed to load connection: ${e}`);
            }
        }

        // Add a timeout to prevent hanging, but clear it once init settles.
        let initTimeoutId: number | undefined;
        const initPromise = bitrix24
          .init()
          .then((result) => {
            if (initTimeoutId !== undefined) {
              window.clearTimeout(initTimeoutId);
            }
            return result;
          })
          .catch((error) => {
            if (initTimeoutId !== undefined) {
              window.clearTimeout(initTimeoutId);
            }
            throw error;
          });

        const timeoutPromise = new Promise<boolean>((resolve) => {
          initTimeoutId = window.setTimeout(() => {
            console.warn('Bitrix24 init timed out');
            resolve(false);
          }, 5000);
        });

        const isInBitrix = await Promise.race([initPromise, timeoutPromise]);
        if (initTimeoutId !== undefined) {
          window.clearTimeout(initTimeoutId);
        }
        const bitrixSdkPresent = bitrix24.isInBitrix24();
        const canUseBitrixFallback = !isInBitrix && bitrixSdkPresent;
        setDebugInfo(`In Bitrix: ${isInBitrix || canUseBitrixFallback}`);

        if (isInBitrix || canUseBitrixFallback) {
          // Get auth data from Bitrix24
          setDebugInfo('Getting auth data...');
          const authData = await bitrix24.getAuthData();
          console.log('Bitrix24 Auth:', authData);
          setDebugInfo(`Auth data: ${JSON.stringify(authData)}`);

          // Keep backend OAuth tokens in sync with iframe auth (self-healing without reinstall).
          if (authData?.access_token && authData?.domain) {
            await syncBitrixAuthPayload(
              {
                domain: authData.domain,
                member_id: authData.member_id,
                auth: authData,
              },
              'sdk-current',
            );
          }

          // Immediately force a refresh when the current iframe payload is not
          // enough to maintain the backend OAuth chain (for example, missing
          // refresh_token after a previous invalid_grant).
          await syncBitrixSdkAuth('sdk-boot', { refreshIfNeeded: true });

          // Periodic OAuth self-healing while app stays open in Bitrix iframe.
          // Refreshes auth in SDK and syncs latest tokens to backend before expiration.
          if (bitrixAuthSyncInterval.current !== null) {
            window.clearInterval(bitrixAuthSyncInterval.current);
            bitrixAuthSyncInterval.current = null;
          }

          bitrixAuthSyncInterval.current = window.setInterval(async () => {
            try {
              const currentAuth = await bitrix24.getAuthData();
              await syncBitrixAuthPayload(
                currentAuth?.access_token && currentAuth?.domain
                  ? {
                      domain: currentAuth.domain,
                      member_id: currentAuth.member_id,
                      auth: currentAuth,
                    }
                  : null,
                'sdk-periodic-current',
              );

              if (shouldRefreshBitrixIframeAuth(currentAuth)) {
                await syncBitrixSdkAuth('sdk-periodic', { forceRefresh: true });
              }
            } catch (error) {
              console.warn('Periodic Bitrix OAuth sync failed (non-blocking):', error);
            }
          }, BITRIX_AUTH_SYNC_INTERVAL_MS);
          
          // Auto-resize can trigger iframe/container refresh in some Bitrix setups.
          // Keep it opt-in via env var.
          if (ENABLE_BITRIX_AUTO_RESIZE) {
            bitrix24.resizeFrame();
          }

           // installFinish is handled by backend install endpoint response.
           // Calling it here during normal app loads can trigger unwanted iframe refreshes.
          
          // Load tenant data based on portal
          if (authData?.domain) {
            try {
              console.log('Fetching tenant for domain:', authData.domain);
              setDebugInfo('Carregando ambiente...');
              const tenantData = await api.getTenantByDomain(authData.domain);
              if (tenantData) {
                console.log('Found tenant for domain:', authData.domain, tenantData.id);
                localStorage.setItem('tenantId', tenantData.id);
                localStorage.removeItem('actingTenantId');
                await fetchAll(tenantData.id);
              } else {
                console.error('Tenant not found for domain:', authData.domain);
                setDebugInfo('Tenant not found - app may need reinstall');
                setInitError(`Tenant not found for domain: ${authData.domain}. Please reinstall the app.`);
              }
            } catch (e) {
              console.error('Failed to fetch tenant by domain:', e);
              setDebugInfo(`Error fetching tenant: ${e}`);
              setInitError(`Failed to load tenant: ${e}`);
            }
          } else {
            // No auth domain but we're in Bitrix - try saved tenantId
            if (savedTenantId) {
              setDebugInfo('Using saved tenantId...');
              try {
                await fetchAll(savedTenantId);
              } catch (e) {
                console.error('Failed to load saved tenant:', e);
                localStorage.removeItem('tenantId');
                setInitError(`Tenant não encontrado. Por favor, reinstale o app.`);
              }
            } else {
              // No saved tenant and no auth domain - show error
              setInitError('Não foi possível identificar o tenant. Reinstale o app.');
            }
          }
        } else {
          // Running standalone (development mode) or Bitrix init failed
          console.log('Running in standalone mode or Bitrix init failed');
          setDebugInfo('Standalone mode');
          
          // Try saved tenantId for development
          if (savedTenantId) {
            setDebugInfo('Loading saved tenant...');
            try {
              await fetchAll(savedTenantId);
            } catch (e) {
              console.error('Failed to load saved tenant:', e);
              localStorage.removeItem('tenantId');
              setInitError(`Sessão expirada ou tenant não encontrado. Reinstale o app.`);
            }
          } else {
            // No saved tenant and not in Bitrix: render public landing page.
            setDebugInfo('Standalone mode - public landing');
            setInitError(null);
          }
        }
      } catch (error) {
        console.error('Error initializing Bitrix24:', error);
        setDebugInfo(`Init error: ${error}`);
        localStorage.removeItem('tenantId'); // Clear invalid cache
        setInitError(`Initialization error: ${error}`);
      }
    };

    initBitrix().finally(() => {
      const elapsed = Date.now() - loadingStartedAt.current;
      const remaining = Math.max(0, MIN_APP_LOADING_MS - elapsed);

      initCompleteTimeout.current = window.setTimeout(() => {
        setInitComplete(true);
      }, remaining);
    });

    return () => {
      if (initCompleteTimeout.current !== null) {
        window.clearTimeout(initCompleteTimeout.current);
      }
      if (bitrixAuthSyncInterval.current !== null) {
        window.clearInterval(bitrixAuthSyncInterval.current);
        bitrixAuthSyncInterval.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!tenant?.id) {
      return;
    }

    let disposed = false;

    const syncTenantStatus = async () => {
      if (tenantSyncInFlight.current || disposed) {
        return;
      }

      tenantSyncInFlight.current = true;
      try {
        const latestTenant = await api.getTenant(tenant.id);
        if (!disposed) {
          const currentTenant = useAppStore.getState().tenant;
          const hasMeaningfulChange =
            !currentTenant ||
            currentTenant.status !== latestTenant.status ||
            currentTenant.subscriptionPlan !== latestTenant.subscriptionPlan ||
            currentTenant.trialEndsAt !== latestTenant.trialEndsAt ||
            currentTenant.messageCountThisMonth !== latestTenant.messageCountThisMonth ||
            currentTenant.messageLimit !== latestTenant.messageLimit;

          if (hasMeaningfulChange) {
            setTenant(latestTenant);
          }
        }
      } catch (error) {
        console.warn('Tenant status sync failed:', error);
      } finally {
        tenantSyncInFlight.current = false;
      }
    };

    const intervalId = window.setInterval(() => {
      void syncTenantStatus();
    }, TENANT_SYNC_INTERVAL_MS);

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
    };
  }, [tenant?.id, setTenant]);

  // Always show something - never stay blank
  const displayError = initError || storeError;

  // Show loading only during initial load, with a max timeout
  if (!initComplete) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="flex flex-col items-center justify-center h-screen bg-slate-50 dark:bg-slate-950"
        >
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="h-40 w-40 mb-4"
          >
            <DotLottieReact
              src={APP_LOADING_LOTTIE_URL}
              loop
              autoplay
              className="h-full w-full"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.08, ease: 'easeOut' }}
            className="text-slate-500 dark:text-slate-300 text-sm"
          >
            {debugInfo}
          </motion.div>
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.15, ease: 'easeOut' }}
            onClick={() => {
              localStorage.removeItem('tenantId');
              triggerAppReload('loading-clear-cache');
            }}
            className="mt-4 text-xs text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-200 underline"
          >
            Travou? Clique para limpar cache
          </motion.button>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Show error state with retry option
  if (displayError && !tenant) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-50 dark:bg-slate-950">
        <div className="bg-red-50 dark:bg-slate-900 border border-red-200 dark:border-slate-700 rounded-lg p-6 max-w-md text-center">
          <div className="text-red-600 dark:text-red-400 text-lg font-semibold mb-2">Erro ao carregar</div>
          <div className="text-red-500 dark:text-red-300 text-sm mb-4">{displayError}</div>
          <div className="text-slate-500 dark:text-slate-300 text-xs mb-4">Debug: {debugInfo}</div>
          <button 
            onClick={() => triggerAppReload('error-retry')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 mr-2"
          >
            Tentar Novamente
          </button>
          <button 
            onClick={() => {
              localStorage.removeItem('tenantId');
              triggerAppReload('error-clear-cache');
            }}
            className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-100 px-4 py-2 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600"
          >
            Limpar Cache
          </button>
        </div>
      </div>
    );
  }

  const isBillingBlocked =
    tenant?.status === 'expired' ||
    tenant?.status === 'suspended' ||
    tenant?.status === 'cancelled' ||
    (tenant?.status === 'trial' && tenant?.trialEndsAt && new Date() > new Date(tenant.trialEndsAt));

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin" element={<Admin />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        {!tenant ? (
          <Route path="*" element={<Home />} />
        ) : isBillingBlocked ? (
          <>
            <Route path="/subscription" element={<Subscription />} />
            <Route path="*" element={<Navigate to="/subscription" replace />} />
          </>
        ) : (
          <>
            <Route path="/subscription" element={<Subscription />} />
            {/* Flow Builder has its own layout */}
            <Route path="/flows/:id" element={<FlowBuilder />} />
            
            {/* Main app with sidebar layout */}
            <Route element={<Layout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/connections" element={<Connections />} />
              <Route path="/bots" element={<Bots />} />
              <Route path="/flows" element={<Flows />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
          </>
        )}
      </Routes>
      {tenant && !isBillingBlocked && <WhatsNewModal />}
    </BrowserRouter>
  );
}

export default App;
